import { useEffect, useState } from "react";

import { EUserDocumentType, TUserDocument } from "../@types";
import { apis, callApi } from "../api";
import { useAccount } from "./useAccount";
import { eventNames, logEvent, TEventNames } from "../utils/logEvent";

export type TUserDocuments = {
	[EUserDocumentType.EMERGENCY_CONTACT]: TUserDocument[];
	[EUserDocumentType.ID_CARD]: TUserDocument[];
	[EUserDocumentType.PILOT_CERTIFICATE]: TUserDocument[];
};

export type TUserDocumentsService = {
	listeners: (() => void)[];
	loading: boolean;
	userDocuments: TUserDocuments;

	addEventListener: (cb: () => void) => void;
	clearData: () => void;
	emitChanges: () => void;
	removeEventListener: (cb: () => void) => void;

	setLoading: (loading: boolean) => void;
	setUserDocuments: (userDocuments: TUserDocuments) => void;
	addUserDocument: (
		type: EUserDocumentType,
		userDocument: TUserDocument,
	) => void;
	removeUserDocument: (type: EUserDocumentType, id: number) => void;
};

export const UserDocumentsService: TUserDocumentsService = {
	listeners: [],
	loading: false,
	userDocuments: {
		[EUserDocumentType.EMERGENCY_CONTACT]: [],
		[EUserDocumentType.ID_CARD]: [],
		[EUserDocumentType.PILOT_CERTIFICATE]: [],
	},

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	clearData() {
		this.loading = false;
		this.userDocuments = {
			[EUserDocumentType.EMERGENCY_CONTACT]: [],
			[EUserDocumentType.ID_CARD]: [],
			[EUserDocumentType.PILOT_CERTIFICATE]: [],
		};
	},

	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},

	setLoading(loading) {
		this.loading = loading;
	},

	setUserDocuments(userDocuments) {
		this.userDocuments = userDocuments;
	},

	addUserDocument(type, userDocument) {
		this.userDocuments[type] = [...this.userDocuments[type], userDocument];
		this.userDocuments = { ...this.userDocuments };
	},

	removeUserDocument(type, id) {
		this.userDocuments[type] = this.userDocuments[type].filter(
			(userDocument) => userDocument.id !== id,
		);
		this.userDocuments = { ...this.userDocuments };
	},
};

export function useUserDocumentsInit() {
	useEffect(() => {
		const getAllMyDocuments = async () => {
			try {
				UserDocumentsService.setLoading(true);
				UserDocumentsService.emitChanges();
				const response = await callApi({
					method: "GET",
					url: apis.GET_ALL_MY_DOCUMENTS,
				});
				if (!response?.documents) {
					return;
				}

				const myPilotCertificateDocuments = [];
				const myIdCardDocuments = [];
				const myEmergencyContactDocuments = [];
				for (let i = 0; i < response.documents.length; i++) {
					if (
						response.documents[i].documentType ===
						EUserDocumentType.PILOT_CERTIFICATE
					) {
						myPilotCertificateDocuments.push(response.documents[i]);
					} else if (
						response.documents[i].documentType === EUserDocumentType.ID_CARD
					) {
						myIdCardDocuments.push(response.documents[i]);
					} else if (
						response.documents[i].documentType ===
						EUserDocumentType.EMERGENCY_CONTACT
					) {
						myEmergencyContactDocuments.push(response.documents[i]);
					}
				}

				UserDocumentsService.setLoading(false);
				UserDocumentsService.setUserDocuments({
					[EUserDocumentType.PILOT_CERTIFICATE]: myPilotCertificateDocuments,
					[EUserDocumentType.ID_CARD]: myIdCardDocuments,
					[EUserDocumentType.EMERGENCY_CONTACT]: myEmergencyContactDocuments,
				});
				UserDocumentsService.emitChanges();
			} catch (error) {}
		};

		getAllMyDocuments();
	}, []);
}

export function useUserDocuments() {
	const { account } = useAccount();
	const [loading, setLoading] = useState(UserDocumentsService.loading);
	const [userDocuments, setUserDocuments] = useState(
		UserDocumentsService.userDocuments,
	);

	useEffect(() => {
		const updateData = () => {
			setLoading(UserDocumentsService.loading);
			setUserDocuments(UserDocumentsService.userDocuments);
		};

		UserDocumentsService.addEventListener(updateData);

		return () => {
			UserDocumentsService.removeEventListener(updateData);
		};
	}, []);

	const uploadDocument = async (file: File, type: EUserDocumentType) => {
		if (!account) {
			return;
		}

		try {
			const getSignedURLResponse = await callApi({
				method: "POST",
				url: apis.GET_SIGNED_URL,
				body: {
					fileName: file.name,
					docType: "docs",
					contentType: file.type,
					userId: account.userId,
				},
			});

			if (!getSignedURLResponse) {
				return;
			}

			const { signedURL, location } = getSignedURLResponse;

			await callApi({
				method: "PUT",
				baseURL: "",
				url: signedURL,
				body: file,
			});

			const saveNewDocumentResponse = await callApi({
				method: "POST",
				url: apis.CREATE_MY_DOCUMENT,
				body: {
					fileName: file.name,
					contentType: file.type,
					location,
					documentType: type,
				},
			});

			if (saveNewDocumentResponse?.newDocument) {
				UserDocumentsService.addUserDocument(
					type,
					saveNewDocumentResponse.newDocument,
				);
				UserDocumentsService.emitChanges();

				let event: TEventNames | undefined;
				if (type === EUserDocumentType.PILOT_CERTIFICATE) {
					event = eventNames.submitPilotCertificate;
				} else if (type === EUserDocumentType.ID_CARD) {
					event = eventNames.submitGovernmentId;
				} else if (type === EUserDocumentType.EMERGENCY_CONTACT) {
					event = eventNames.submitEmergencyContact;
				}

				if (event) {
					logEvent(event);
				}
			}
		} catch (error) {}
	};

	const deleteFile = async (id: number, type: EUserDocumentType) => {
		try {
			await callApi({
				method: "DELETE",
				url: `${apis.DELETE_MY_DOCUMENT}/${id}`,
			});

			UserDocumentsService.removeUserDocument(type, id);
			UserDocumentsService.emitChanges();
		} catch (error) {}
	};

	return { loading, userDocuments, uploadDocument, deleteFile };
}
